/**
 * Camada 1 · o chassi completo.
 *
 * ESTE COMPONENTE E O MOTIVO DA PLATAFORMA EXISTIR. Rail, topbar, painel de ajuda e
 * botao sair moram aqui, uma vez. Um produto novo recebe tudo isso configurando um
 * ProductConfig, e nao copiando pasta. Mudar a topbar altera UM arquivo e reflete em
 * todos os produtos, que e a promessa inteira da arquitetura.
 *
 * O QUE NAO ENTRA AQUI: qualquer coisa que um produto especifico precise. No dia em que
 * um `if (produto.id === 'x')` aparecer neste arquivo, a promessa acabou, e o certo e
 * ampliar o ProductConfig em vez de ramificar aqui.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { ProductConfig } from '@complexity/product-kit';
import type { DadoDaConta } from './Rail.js';
import type { Ambiente } from '@complexity/config';
import { t } from '@complexity/i18n';
import { BarraInferior } from './BarraInferior.js';
import { FaixaAmbiente } from '@complexity/ui';
import { Rail } from './Rail.js';
import { Topbar, type OpcaoUsuario } from './Topbar.js';

export interface AppShellProps {
  readonly produto: ProductConfig;
  /**
   * As permissoes de quem esta olhando, para o menu esconder o que ela nao alcanca.
   *
   * AUSENTE VALE "nenhuma permissao especial", e nao "todas": item restrito some quando o
   * app ainda nao sabe o papel, e reaparece quando souber. O contrario deixaria o item
   * visivel durante a descoberta, e um piscar de item administrativo e pior que a espera.
   *
   * MEDIDO EM 29/08/2026: o campo `permissoes` do `ItemMenu` existia no contrato e nenhum
   * componente o aplicava, entao `/usuarios` aparecia para todo membro do tenant. No
   * legado, `somenteAdmin: true` esconde o item (linha V17 da paridade).
   *
   * ISTO NAO E A TRANCA, e sim a cortesia: quem decide o que a pessoa pode fazer e o RLS,
   * no banco. Esconder o item evita oferecer o que vai ser recusado, e nada mais.
   */
  readonly permissoesDoUsuario?: readonly string[];
  /**
   * Em que ambiente este produto esta rodando, para a faixa permanente do topo.
   *
   * OPCIONAL NA ASSINATURA, e mesmo assim IMPOSSIVEL DE DESLIGAR: omitir nao apaga a
   * faixa, faz aparecer a faixa vermelha de ambiente nao declarado. O unico jeito de a
   * chassi subir sem faixa e alguem escrever `prd`, de proposito. A propriedade e
   * opcional porque hoje nenhum app tem de onde tirar o ambiente no navegador, e um
   * campo obrigatorio agora forcaria cada app a inventar um valor, que e pior que
   * declarar que nao sabe.
   */
  readonly ambiente?: Ambiente;
  readonly rotaAtiva: string;
  readonly aoNavegar: (rota: string) => void;
  readonly nomeUsuario: string;
  readonly opcoesUsuario?: readonly OpcaoUsuario[];
  readonly aoSair: () => void;
  readonly naoLidos?: number;
  readonly aoAbrirAvisos: () => void;
  /**
   * O nome falado do sino, já pronto, repassado sem mudança para a Topbar. Quem monta é o
   * chassi, a partir de `rotuloDoSino` do manifesto. Ausente, a Topbar diz "Avisos".
   */
  readonly rotuloDoSino?: string;
  /**
   * As linhas do painel da conta, quando o produto liga `sairNaConta`. Vem de fora pelo mesmo
   * motivo da ajuda: o chassi nao conhece o cadastro da pessoa, e o produto conhece.
   */
  readonly dadosDaConta?: readonly DadoDaConta[];
  /** O painel de ajuda vem de fora, ja preso ao namespace do produto, para o chassi nao
   *  precisar conhecer a base de conhecimento. */
  /**
   * A GAVETA DE BUSCA RAPIDA que o botao `?` do cabecalho abre.
   *
   * ELA DEIXOU DE SER OPCIONAL EM 06/09/2026, e a mudanca e uma TRANCA, nao um ajuste de
   * estilo. O DEFEITO que ela fecha, medido em 05/09/2026: o `admin` e o `_template`
   * montavam este componente sem passar o painel, o typecheck aceitava porque o campo era
   * opcional, e a `Topbar` desenhava o `?` SEM CONDICAO. O resultado na tela era um botao
   * da plataforma que a pessoa apertava e nao respondia.
   *
   * OBRIGATORIO, o app que esquecer NAO COMPILA, e o defeito deixa de depender de alguem
   * lembrar numa revisao. Hoje nao quebra ninguem: o unico lugar que monta o `AppShell` e
   * o `Chassi`, e ele ja passa o painel (packages/chassi/src/Chassi.tsx:170).
   */
  /**
   * ELA ACEITA UMA FUNCAO, e nao so um no pronto, e isso conserta um defeito de tela.
   *
   * O DEFEITO, relatado por ele em 09/09/2026: "BOTAO X DA AJUDA NAO ESTA FUNCIONANDO". A
   * gaveta tem um X proprio (`PainelAjuda.tsx:142`), e quem monta o painel nao tinha como
   * fecha-la, porque `ajudaAberta` e estado PRIVADO daqui. O chassi acabava passando uma
   * funcao vazia, e o X ficava com a aparencia de clicavel ligada e sem fazer nada. O Escape
   * caiu junto, pela mesma prop.
   *
   * POR QUE FUNCAO E NAO CONTEXTO: as duas resolvem, e a funcao nao acrescenta no nenhum na
   * arvore. Este componente e o chassi de TODOS os produtos, e um Provider a mais aqui entra
   * em todas as telas da plataforma para servir a um painel so.
   *
   * O FORMATO ANTIGO CONTINUA VALENDO: quem passa um no pronto nao precisa mudar nada, e o
   * typecheck aceita os dois.
   */
  readonly painelAjuda: ReactNode | ((aoFechar: () => void) => ReactNode);
  /**
   * O painel de conversas, que o sino abre.
   *
   * VEM DE FORA pelo mesmo motivo da ajuda: o chassi nao conhece o servico de mensagens, e
   * nao deve. Ausente, o sino continua desenhando o numero e o clique cai em
   * `aoAbrirAvisos`: nenhum produto quebra por nao passar este painel.
   */
  readonly painelAvisos?: ReactNode;
  readonly children: ReactNode;
}

export function AppShell({
  produto,
  permissoesDoUsuario,
  ambiente,
  rotaAtiva,
  aoNavegar,
  nomeUsuario,
  opcoesUsuario = [],
  aoSair,
  naoLidos = 0,
  aoAbrirAvisos,
  rotuloDoSino,
  dadosDaConta,
  painelAjuda,
  painelAvisos,
  children,
}: AppShellProps) {
  // A ESCOLHA DE RETRAIR O MENU FICA GRAVADA. Linha V21 da paridade do prontidao, e entrou
  // em 29/08/2026. O motivo esta no proprio legado: "o despachante abre o sistema dezenas de
  // vezes por dia e nao quer reajustar o menu a cada abertura".
  //
  // O MENU NASCE ABERTO, e a comparacao abaixo e contra 'retraido' e NAO a favor de
  // 'expandido'. A diferenca e o comportamento inteiro: quem nunca escolheu nao tem valor
  // gravado, e a ausencia de escolha precisa cair no padrao (aberto), e nao no oposto. Foi
  // exatamente assim que o legado resolveu, e reproduzir a comparacao e reproduzir a decisao.
  //
  // A chave leva o id do produto: dois produtos no mesmo dominio compartilham
  // `localStorage`, e sem o id a escolha feita num deles mudaria o menu do outro.
  /**
   * O MENU JA FILTRADO, e o filtro acontece UMA vez, antes dos dois lugares que o desenham.
   * Filtrar dentro do rail e dentro da barra separadamente e como as duas divergem.
   */
  const menuVisivel = produto.menu.filter((item) => {
    // Sem exigencia declarada, o item e de todo membro do tenant.
    if (!item.permissoes || item.permissoes.length === 0) return true;
    // TODAS as exigidas, e nao alguma: um item que pede duas permissoes pede as duas.
    return item.permissoes.every((p) => (permissoesDoUsuario ?? []).includes(p));
  });

  const chaveDoMenu = `cx.${produto.id}.menu`;

  const [colapsado, setColapsado] = useState<boolean>(() => {
    try {
      return localStorage.getItem(chaveDoMenu) === 'retraido';
    } catch {
      // Janela anonima recusa a leitura, e ai vale o padrao de primeira visita: aberto.
      return false;
    }
  });
  const [gavetaAberta, setGavetaAberta] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [avisosAbertos, setAvisosAbertos] = useState(false);

  /**
   * O FECHAMENTO QUE A GAVETA RECEBE, e ele e estavel de proposito.
   *
   * O painel guarda esta funcao numa dependencia de efeito (`PainelAjuda.tsx:98`), que e o
   * que liga a tecla Escape. Recriada a cada render, ela faria o efeito desassinar e
   * reassinar o listener do documento a cada quadro, sem ninguem perceber.
   */
  const fecharAjuda = useCallback(() => setAjudaAberta(false), []);

  /**
   * LINHA V23 DA PARIDADE: no TABLET o menu e sempre tira de icones, e a preferencia gravada
   * e ignorada NA HORA DE DESENHAR, mas PRESERVADA para voltar no desktop.
   *
   * O motivo esta no legado (`Shell.tsx:41`): com tres frames em 768px, a largura que sobra
   * nao comporta lista e detalhe legiveis ao mesmo tempo que um menu por extenso. Nao e
   * preferencia de estilo, e aritmetica de largura.
   *
   * POR QUE ISTO NAO PODE SER SO CSS, que seria o caminho obvio aqui: esconder o rotulo com
   * `md:` deixaria o menu OCUPANDO a largura, so que com texto invisivel. O que o legado faz
   * e mudar o MODELO do menu, e isso o CSS nao alcanca.
   *
   * POR QUE A PREFERENCIA NAO E SOBRESCRITA: quem trabalha no desktop com o menu aberto e
   * abre o mesmo sistema no tablet nao esta mudando de ideia. Gravar `retraido` aqui faria a
   * visita ao tablet apagar a escolha do desktop, em silencio, e a pessoa voltaria no dia
   * seguinte com o menu que ela nunca pediu.
   *
   * Os cortes sao os mesmos do legado (`faixa.ts:9-10`): tablet e de 768px a 1279px.
   */
  const [ehTablet, setEhTablet] = useState<boolean>(() => {
    try {
      return window.matchMedia('(min-width: 768px) and (max-width: 1279px)').matches;
    } catch {
      // Ambiente sem matchMedia (renderizacao no servidor, teste de unidade) cai no caminho
      // normal: melhor o menu comum que uma tela que nao desenha.
      return false;
    }
  });

  useEffect(() => {
    // `matchMedia` e nao `resize`: o navegador so avisa quando a faixa VIRA outra, entao nao
    // ha um render por pixel arrastado na borda da janela. E o mesmo motivo do legado.
    const consulta = window.matchMedia('(min-width: 768px) and (max-width: 1279px)');
    const aoMudar = (e: MediaQueryListEvent) => setEhTablet(e.matches);
    consulta.addEventListener('change', aoMudar);
    // Uma medicao imediata cobre a janela ter mudado entre o primeiro render e este efeito.
    setEhTablet(consulta.matches);
    return () => consulta.removeEventListener('change', aoMudar);
  }, []);

  /**
   * LINHA V24 DA PARIDADE: trocar de rota fecha a gaveta, e isso INCLUI o botao voltar do
   * navegador, que nao passa pelo clique nos itens do menu.
   *
   * O `aoNavegar` do rail ja fechava a gaveta no clique, e era so metade: quem abre a gaveta,
   * navega, e depois usa o voltar, encontrava a gaveta aberta cobrindo a tela que acabou de
   * pedir. Dois caminhos para a mesma navegacao, e so um estava coberto.
   *
   * A COMPARACAO ACONTECE DURANTE O RENDER, e nao em efeito, pelo mesmo motivo escrito no
   * legado (`Shell.tsx:56-59`): ajustar estado local em resposta a mudanca de prop nao e
   * sincronizar com sistema externo, e em efeito custaria um segundo render em cascata a cada
   * navegacao. E o padrao que o React documenta para estado derivado de prop.
   */
  const [rotaAnterior, setRotaAnterior] = useState(rotaAtiva);
  if (rotaAnterior !== rotaAtiva) {
    setRotaAnterior(rotaAtiva);
    if (gavetaAberta) setGavetaAberta(false);
  }

  /**
   * LINHA V25 DA PARIDADE: `Esc` fecha a gaveta.
   *
   * E o gesto esperado de qualquer camada sobreposta, e sem ele quem navega por teclado fica
   * preso dentro da gaveta: o resto da tela esta atras dela, e nao ha como sair sem mouse.
   *
   * O listener so existe ENQUANTO a gaveta esta aberta. Deixa-lo sempre ligado faria a tecla
   * ser inspecionada em toda digitacao do sistema, inclusive dentro de campo de texto.
   */
  useEffect(() => {
    if (!gavetaAberta) return;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setGavetaAberta(false);
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [gavetaAberta]);

  // O que DESENHA. A preferencia (`colapsado`) continua intacta, e e ela que o efeito grava.
  const colapsadoNaTela = ehTablet ? true : colapsado;

  // A gravacao vai em efeito, e nao dentro do `onClick`: assim ela acompanha qualquer
  // caminho que mude o estado, inclusive um que alguem acrescente depois. Escrever no
  // manipulador funciona hoje e deixa de funcionar no dia em que houver um segundo caminho.
  useEffect(() => {
    try {
      localStorage.setItem(chaveDoMenu, colapsado ? 'retraido' : 'expandido');
    } catch {
      // Sem armazenamento a escolha vale so nesta aba, e isso e melhor que quebrar a tela.
    }
  }, [chaveDoMenu, colapsado]);

  // O titulo sai do proprio menu, e nao de um campo separado: duas fontes para o mesmo
  // texto divergem, e a que fica errada e a que ninguem olha.
  //
  // A ROTA DA MARCA NAO TEM ROTULO PROPRIO, e cai no nome do produto de proposito (V26).
  // No prontidao ela leva a "Sobre o sistema", que e uma tela SOBRE o produto: mostrar o
  // nome dele na topbar ali e a leitura certa, e nao uma queda por falta de dado. Um campo
  // de rotulo separado seria uma segunda fonte para o mesmo texto, e a que fica errada e
  // sempre a que ninguem olha.
  // A QUEDA E PARA O NOME POR EXTENSO, e nao para o curto. Pedido dele em 07/09/2026,
  // olhando a tela: "O PRIMEIRO MEDICINA TB VAI SER MEDICINA CONCIERGE".
  //
  // A REGRA INTEIRA, e ela e de ESPACO e nao de gosto: o rail usa o nome CURTO porque la o
  // comprido cortava (240px menos simbolo, botao e paddings deixam ~140px); a barra do topo
  // usa o POR EXTENSO porque ela tem a largura da tela toda.
  //
  // INOCUO PARA QUEM NAO DECLARA `nomeCompleto`, e isso importa por causa do congelamento:
  // o VOE nao o declara, entao ele degrada para `produto.nome` e a topbar dele continua
  // exatamente como estava no retrato de 06/09/2026.
  //
  // E ELE PASSA POR `t()`, COMO O RAIL JA FAZIA desde sempre (Rail.tsx, linha 401).
  //
  // MEDIDO EM 09/09/2026: sem esta chamada, o rail traduzia e a topbar nao, e a MESMA rota
  // aparecia com DOIS nomes na mesma tela inglesa. Nove das dez rotas do medicina reprovavam
  // o `qa-plt-lang` por causa desta linha, com a traducao ja escrita no dicionario.
  //
  // SEGURO PARA O CONGELAMENTO DO VOE, e a medicao e o que autorizou a mudanca:
  // packages/i18n/src/index.ts, linha 198, faz `if (idioma === IDIOMA_PADRAO) return frase`.
  // Em portugues a funcao devolve a frase INTACTA, e o retrato congelado do VOE e medido em
  // portugues. Dos 14 labels do menu do VOE, apenas TRES tem entrada no dicionario, e as
  // tres so valem em ingles.
  //
  // A PRIMEIRA TENTATIVA DESTA LINHA FOI REVERTIDA POR ENGANO, e o registro fica: a suite do
  // congelamento reprovou, e eu conclui que a mudanca a tinha quebrado. Ela JA ESTAVA
  // vermelha, por uma tela intermediaria que entrou em 07/09 e que ela nao atravessava.
  const tituloTela = t(
    menuVisivel.find((i) => i.rota === rotaAtiva)?.label ??
      produto.nomeCompleto ??
      produto.nome,
  );

  // Os itens da barra inferior do celular. O filtro acontece aqui, uma vez, e nao dentro do
  // componente: a barra recebe a lista pronta e nao precisa conhecer o contrato do produto.
  const itensDaBarra = menuVisivel.filter((i) => i.naBarraInferior);

  return (
    // A RAIZ E UMA COLUNA, e nao mais uma linha: a faixa de ambiente e a primeira linha
    // dela, e todo o resto do chassi e a segunda. Assim o espaco da faixa e reservado pelo
    // proprio layout, sem nenhuma altura compensada a mao em lugar nenhum.
    //
    // `overflow-hidden` fica so aqui, e `min-h-0` na segunda linha: e o par que faz o
    // `main` la dentro rolar sozinho em vez de esticar a coluna para fora da janela. Sem
    // o `min-h-0`, um conteudo alto empurraria a faixa para cima e para fora da tela.
    <div className="flex h-screen flex-col overflow-hidden bg-superficie">
      <FaixaAmbiente ambiente={ambiente} />

      <div className="flex min-h-0 flex-1">
        <Rail
          // O CABECALHO DO MENU POR PRODUTO, e ausente o desenho e o de sempre: o VOE nao declara
          // `cabecalhoDoMenu`, entao o nome, o simbolo e o botao dele ficam como no retrato de
          // 06/09/2026. Nasceu no Medicina em 12/09/2026 (ver `CabecalhoDoMenu` no product-kit).
          nomeProduto={
            produto.cabecalhoDoMenu?.nomePorExtenso
              ? (produto.nomeCompleto ?? produto.nome)
              : produto.nome
          }
          simboloCompacto={produto.cabecalhoDoMenu?.simboloCompacto}
          cabecalhoRecolhe={produto.cabecalhoDoMenu?.recolheAoClicar}
          assinaturaNoMenu={produto.assinaturaNoMenu}
          sairNaConta={produto.sairNaConta}
          dadosDaConta={dadosDaConta}
          itens={menuVisivel}
          rotaAtiva={rotaAtiva}
          aoNavegar={(r) => {
            aoNavegar(r);
            // Fecha a gaveta ao navegar no celular: deixa-la aberta cobriria a tela que a
            // pessoa acabou de pedir.
            setGavetaAberta(false);
          }}
          colapsado={colapsadoNaTela}
          aoAlternar={() => setColapsado((v) => !v)}
          // No tablet o desenho e sempre tira (V23), entao o controle nao teria efeito e nao
          // e desenhado. A PREFERENCIA continua sendo gravada pelo efeito acima, e por isso
          // quem volta ao computador reencontra o menu como deixou.
          alternarTemEfeito={!ehTablet}
          abertoNoCelular={gavetaAberta}
          aoFecharNoCelular={() => setGavetaAberta(false)}
          // V26: a marca so vira botao quando o produto diz para onde ela leva. Ausente, ela
          // continua sendo apenas identidade, e nao um botao que nao faz nada.
          rotaDaMarca={produto.rotaDaMarca}
          // O RODAPE DA CONTA, movido da topbar em 30/08/2026. As tres props sao as mesmas
          // que a topbar recebia; o que mudou foi o componente que as desenha.
          nomeUsuario={nomeUsuario}
          opcoesUsuario={opcoesUsuario}
          aoSair={aoSair}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar
            titulo={tituloTela}
            // O SAIR NAO VAI MAIS PARA A TOPBAR desde 15/09/2026, pedido do fundador: "tirar o sair
            // do cabecalho do chassi". Ele mora so no rodape da conta, que recebe `aoSair` acima.
            naoLidos={naoLidos}
            rotuloDoSino={rotuloDoSino}
            // Com painel, o sino ABRE o painel; sem, cai no callback de antes. Os dois
            // caminhos existem porque nem todo produto tem conversa ligada, e um sino que
            // nao faz nada ao ser clicado e pior que sino nenhum.
            aoAbrirAvisos={() => {
              if (painelAvisos) {
                setAvisosAbertos((v) => !v);
                // A AJUDA FECHA quando os avisos abrem, e vice-versa: os dois ocupam a MESMA
                // coluna lateral, e no celular a mesma tela inteira. Abertos juntos, um
                // cobriria o outro sem ninguem entender o que aconteceu.
                setAjudaAberta(false);
              }
              aoAbrirAvisos();
            }}
            // O manifesto decide se o icone de ajuda existe (fundador, 07/10/2026).
            mostrarAjuda={produto.ajuda.noCabecalho !== false}
            aoAbrirAjuda={() => {
              setAjudaAberta((v) => !v);
              setAvisosAbertos(false);
            }}
            aoAbrirMenu={() => setGavetaAberta(true)}
          />

          <div className="relative flex min-h-0 flex-1">
            <main className="min-w-0 flex-1 overflow-y-auto p-cx-4">{children}</main>

            {avisosAbertos && painelAvisos && (
              <aside className="absolute inset-0 z-painel overflow-y-auto border-l border-borda bg-superficie-elevada md:static md:inset-auto md:w-80">
                {painelAvisos}
              </aside>
            )}

            {ajudaAberta && painelAjuda && (
              // No celular o painel ocupa a tela inteira; do tablet para cima, uma coluna
              // lateral. Sem isso, uma coluna de 20rem em 390px sobra dois centimetros para
              // o conteudo.
              <aside className="absolute inset-0 z-painel overflow-y-auto border-l border-borda bg-superficie-elevada p-cx-4 md:static md:inset-auto md:w-80">
                {/* Quem passou uma funcao recebe o fechamento; quem passou um no pronto
                    continua desenhando igual, e nao ganha o X funcionando. */}
                {typeof painelAjuda === 'function' ? painelAjuda(fecharAjuda) : painelAjuda}
              </aside>
            )}
          </div>

          {/* A BARRA INFERIOR fica FORA do <div> que rola, e depois dele: assim ela nao sobe
              junto com o conteudo e continua alcancavel com o polegar em qualquer ponto da
              rolagem. Barra que rola com a pagina e barra que some quando mais se precisa. */}
          <BarraInferior itens={itensDaBarra} rotaAtiva={rotaAtiva} aoNavegar={aoNavegar} />
        </div>
      </div>
    </div>
  );
}
